import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useTenantScope } from '../context/TenantScopeContext.jsx';
import EmployeeRosterModal from '../components/EmployeeRosterModal.jsx';
import AreasManagerModal from '../components/AreasManagerModal.jsx';
import {
    emptyEmployeeForm,
    employeeFormFromRecord,
    buildCreateEmployeePayload,
    buildUpdateEmployeePayload,
    validateRosterForm,
} from '../utils/employeeRosterForm.js';
import { FEELINGS } from '../utils/constants.js';
import { formatCOP } from '../utils/money.js';
import { unwrapList } from '../utils/apiData.js';
import { applyEmployeeListVisibility } from '../utils/seedVisibility.js';
import './Team.css';

const TEAM_MODAL_BACKDROP = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10000,
    backgroundColor: 'rgba(15, 23, 42, 0.55)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))',
    paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
    paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
    paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 0px))',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
};

const TEAM_MODAL_PANEL = {
    width: 'min(100%, 500px)',
    maxWidth: 500,
    marginTop: 'min(3vh, 1.25rem)',
    marginBottom: 'auto',
    flexShrink: 0,
    alignSelf: 'flex-start',
    maxHeight: 'min(88vh, calc(100dvh - 2.5rem))',
    overflowY: 'auto',
    borderRadius: 14,
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.2)',
};

const ROLE_LABELS = {
    worker: 'Colaborador',
    supervisor: 'Supervisor',
    admin: 'Administrador',
};

function feelingBadge(emp) {
    if (!emp.latestFeeling) {
        return <span className="badge badge-gray text-xs">Sin check-in</span>;
    }
    const f = FEELINGS[emp.latestFeeling];
    const tone = emp.latestFeeling === 'bien' ? 'green' : emp.latestFeeling === 'regular' ? 'yellow' : 'red';
    return (
        <span className={`badge badge-${tone}`}>
            {f?.emoji} {f?.label}
        </span>
    );
}

export default function Team() {
    const { user, platform } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const filterArea = searchParams.get('areaId') || '';
    const filterSupervisor = searchParams.get('supervisorId') || '';
    const showSalary = user?.rol === 'admin' || user?.rol === 'superadmin' || user?.rol === 'supervisor';
    const canEditRoster = user?.rol === 'admin' || user?.rol === 'superadmin';
    const isSupervisorOnly = user?.rol === 'supervisor';
    const [employees, setEmployees] = useState([]);
    const [areas, setAreas] = useState([]);
    const [supervisors, setSupervisors] = useState([]);
    const [reasons, setReasons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [retiringEmp, setRetiringEmp] = useState(null);
    const [causalesModalOpen, setCausalesModalOpen] = useState(false);
    const [newCausal, setNewCausal] = useState('');
    const [causalesSaving, setCausalesSaving] = useState(false);

    const [rosterModal, setRosterModal] = useState(null);
    const [rosterSaving, setRosterSaving] = useState(false);
    const [areasModalOpen, setAreasModalOpen] = useState(false);
    const [areasBusy, setAreasBusy] = useState(false);
    const [viewMode, setViewMode] = useState('cards');
    const [loadError, setLoadError] = useState(null);
    const fileInputRef = useRef(null);

    function updateFilters({ areaId, supervisorId }) {
        const next = new URLSearchParams(searchParams);
        if (areaId) next.set('areaId', areaId);
        else next.delete('areaId');
        if (supervisorId) next.set('supervisorId', supervisorId);
        else next.delete('supervisorId');
        setSearchParams(next, { replace: true });
    }

    function clearFilters() {
        setSearchParams({}, { replace: true });
    }

    const { tenantId, needsTenant } = useTenantScope();

    useEffect(() => {
        if (needsTenant) {
            setLoading(false);
            setEmployees([]);
            setAreas([]);
            return;
        }
        loadData();
    }, [tenantId, needsTenant]);

    async function reloadAreas() {
        try {
            const areaRes = await api.getAdminAreas(tenantId);
            setAreas(unwrapList(areaRes));
        } catch (err) {
            console.error(err);
        }
    }

    async function loadData() {
        setLoading(true);
        setLoadError(null);
        try {
            const [empSettled, areaSettled, supSettled, reasonSettled] = await Promise.allSettled([
                api.getEmployees(tenantId, { lite: true }),
                api.getAdminAreas(tenantId).catch(() => api.getAreaSummary()),
                api.getSupervisors(),
                api.getRetirementReasons(),
            ]);

            if (areaSettled.status === 'fulfilled') {
                setAreas(unwrapList(areaSettled.value));
            } else {
                setAreas([]);
            }

            if (supSettled.status === 'fulfilled') {
                setSupervisors(unwrapList(supSettled.value));
            } else {
                setSupervisors([]);
            }

            if (reasonSettled.status === 'fulfilled') {
                setReasons(unwrapList(reasonSettled.value));
            } else {
                setReasons([]);
            }

            if (empSettled.status === 'fulfilled') {
                setEmployees(
                    applyEmployeeListVisibility(unwrapList(empSettled.value), {
                        seedDataVisible: platform?.seedDataVisible ?? false,
                        viewerRole: user?.rol,
                    })
                );
            } else {
                setEmployees([]);
                setLoadError(
                    empSettled.reason?.message ||
                        'No se pudo cargar el personal. El servidor tardó demasiado; intente de nuevo.'
                );
            }
        } catch (err) {
            console.error(err);
            setLoadError(err.message || 'No se pudo cargar la base de personal.');
            setEmployees([]);
        } finally {
            setLoading(false);
        }
    }

    async function refreshReasons() {
        try {
            const reasonRes = await api.getRetirementReasons();
            const r = Array.isArray(reasonRes) ? reasonRes : reasonRes?.data ?? [];
            setReasons(r);
        } catch (err) {
            console.error(err);
        }
    }

    async function handleAddCausal() {
        const name = newCausal.trim();
        if (!name) return;
        setCausalesSaving(true);
        try {
            await api.createRetirementReason({ name });
            setNewCausal('');
            await refreshReasons();
        } catch (err) {
            alert(err.message || 'No se pudo añadir la causal');
        } finally {
            setCausalesSaving(false);
        }
    }

    async function handleDeleteCausal(id) {
        if (!confirm('¿Eliminar esta causal de retiro? No podrá usarse en nuevas bajas.')) return;
        setCausalesSaving(true);
        try {
            await api.deleteRetirementReason(id);
            await refreshReasons();
        } catch (err) {
            alert(err.message || 'No se pudo eliminar la causal');
        } finally {
            setCausalesSaving(false);
        }
    }

    const handleDownloadTemplate = async () => {
        try {
            await api.downloadEmployeeTemplate();
        } catch (e) {
            alert('Error al descargar la plantilla.');
        }
    };

    const handleExportRoster = async () => {
        try {
            await api.downloadEmployeeRosterExport();
        } catch (e) {
            alert('Error al exportar: ' + (e.message || 'desconocido'));
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        try {
            const res = await api.uploadEmployees(file);
            let msg = res.message || 'Carga completada.';
            if (res.warnings?.length) {
                msg += `\n\nAdvertencias:\n${res.warnings.map((w) => `Fila ${w.row}: ${w.message}`).join('\n')}`;
            }
            alert(msg);
            loadData();
        } catch (err) {
            const details = err.errors?.length
                ? err.errors.map((x) => `Fila ${x.row}: ${x.message}`).join('\n')
                : err.message;
            alert(
                (details || 'Error en carga masiva.') +
                    '\n\nSi ves "+046162-01", reinicia el API: cd server && npm start'
            );
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleCardClick = async (emp) => {
        if (!canEditRoster) {
            navigate(`/team/${emp.id}`);
            return;
        }
        setRosterModal(employeeFormFromRecord(emp));
        try {
            const res = await api.getEmployee(emp.id);
            const row = res?.data ?? res;
            if (row?.id) setRosterModal(employeeFormFromRecord(row));
        } catch {
            /* Mantener datos del listado */
        }
    };

    const openCreateEmployee = () => setRosterModal(emptyEmployeeForm());

    const saveRosterEmployee = async () => {
        if (!rosterModal || rosterSaving) return;
        const validationMsg = validateRosterForm(rosterModal);
        if (validationMsg) {
            alert(validationMsg);
            return;
        }
        setRosterSaving(true);
        try {
            if (rosterModal.mode === 'create') {
                await api.createEmployee(buildCreateEmployeePayload(rosterModal));
            } else {
                await api.updateEmployee(rosterModal.id, buildUpdateEmployeePayload(rosterModal));
            }
            setRosterModal(null);
            try {
                const empRes = await api.getEmployees(tenantId, { lite: true });
                setEmployees(
                    applyEmployeeListVisibility(unwrapList(empRes), {
                        seedDataVisible: platform?.seedDataVisible ?? false,
                        viewerRole: user?.rol,
                    })
                );
                setLoadError(null);
            } catch (reloadErr) {
                console.warn('Lista no recargada tras guardar:', reloadErr);
                setLoadError(
                    reloadErr.message ||
                        'Guardado correcto, pero no se pudo refrescar la lista. Recarga la página.'
                );
            }
            alert('Colaborador guardado correctamente.');
        } catch (err) {
            alert(err.message || 'No se pudo guardar el colaborador');
        } finally {
            setRosterSaving(false);
        }
    };

    const handleCreateArea = async (name) => {
        setAreasBusy(true);
        try {
            await api.createAdminArea({ name }, tenantId);
            await reloadAreas();
        } catch (err) {
            alert(err.message || 'No se pudo crear el área');
        } finally {
            setAreasBusy(false);
        }
    };

    const handleUpdateArea = async (areaId, name) => {
        setAreasBusy(true);
        try {
            await api.updateAdminArea(areaId, { name }, tenantId);
            await reloadAreas();
            await loadData();
        } catch (err) {
            alert(err.message || 'No se pudo actualizar el área');
        } finally {
            setAreasBusy(false);
        }
    };

    const handleDeleteArea = async (area) => {
        const count = Number(area.total_employees ?? 0);
        if (count > 0) {
            alert('No se puede eliminar: hay colaboradores en esta área. Reasígnalos primero.');
            return;
        }
        if (!confirm(`¿Eliminar el área «${area.name}»?`)) return;
        setAreasBusy(true);
        try {
            await api.deleteAdminArea(area.id, tenantId);
            await reloadAreas();
            await loadData();
        } catch (err) {
            alert(err.message || 'No se pudo eliminar el área');
        } finally {
            setAreasBusy(false);
        }
    };

    const handleRetireSubmit = async (e) => {
        e.preventDefault();
        const data = {
            retirement_date: e.target.date.value,
            causal_id: e.target.causal.value,
            position_level: e.target.level.value,
            custom_comments: e.target.comments.value
        };

        try {
            const res = await api.retireEmployee(retiringEmp.id, data);
            const cid = Number(res.calculatedCID || 0).toLocaleString('es-CO');
            let msg = `✅ ${res.message}\n\n💰 CID calculado: $${cid}`;
            if (res.costsEstimated) {
                msg += '\n\nℹ️ Sin costos de rotación para este área/nivel: configura la calculadora para un CID más preciso.';
            }
            alert(msg);
            setRetiringEmp(null);
            setRosterModal(null);
            loadData();
        } catch (err) {
            alert('Error al procesar retiro: ' + (err.response?.data?.error || err.message));
        }
    };

    if (loading && employees.length === 0) return <div className="loading-screen"><div className="spinner" /></div>;

    const filteredEmployees = employees.filter((emp) => {
        if (filterArea && String(emp.area_id) !== String(filterArea)) return false;
        if (filterSupervisor && String(emp.supervisor_id) !== String(filterSupervisor)) return false;
        return true;
    });

    const hasActiveFilters = Boolean(filterArea || filterSupervisor);
    const areaOptions = areas.length
        ? areas
        : [...new Map(employees.map((e) => [e.area_id, { id: e.area_id, name: e.area_name }])).values()].filter(
              (a) => a.id
          );

    const renderEmployeeCard = (emp) => (
        <div
            key={emp.id}
            className="glass-card team-card"
            style={{ position: 'relative', cursor: 'pointer' }}
            onClick={() => handleCardClick(emp)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCardClick(emp);
                }
            }}
            role="button"
            tabIndex={0}
        >
            <div
                style={{ position: 'absolute', top: '10px', right: '10px', opacity: 0.6, fontSize: '1rem' }}
                title={canEditRoster ? 'Gestionar perfil / retiro' : 'Ver ficha'}
            >
                ⚙️
            </div>
            <div className="team-card-header" style={{ paddingRight: '1.5rem' }}>
                <div className="team-card-name">
                    <span className={`status-dot ${emp.latestFeeling || ''}`} />
                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '4px', margin: 0 }}>{emp.full_name}</h4>
                </div>
                <span className="text-xs text-muted">{emp.employee_code}</span>
            </div>
            <div className="team-card-info">
                <span className="text-sm text-muted">📍 {emp.area_name}</span>
                {emp.supervisor_name && (
                    <span className="text-sm text-muted" style={{ display: 'block', marginTop: '0.2rem' }}>
                        👤 {emp.supervisor_name}
                    </span>
                )}
                {showSalary && emp.monthly_salary != null && Number(emp.monthly_salary) > 0 && (
                    <span className="text-sm text-muted" style={{ display: 'block', marginTop: '0.25rem' }}>
                        Sueldo:{' '}
                        <strong style={{ color: 'var(--color-text-primary)' }}>{formatCOP(emp.monthly_salary)}</strong>
                    </span>
                )}
            </div>
            <div className="team-card-status">
                {feelingBadge(emp)}
                {emp.activeAlerts?.map((a, i) => (
                    <span key={i} className={`badge badge-${a.level}`}>
                        {a.count} {a.level === 'red' ? '🔴' : a.level === 'yellow' ? '🟡' : '🟢'}
                    </span>
                ))}
            </div>
        </div>
    );

    return (
        <div className="container container-wide animate-in">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <div>
                    <h2>👥 Base de Personal</h2>
                    <p className="text-muted">
                        {canEditRoster ? 'Estado actual del personal y gestión masiva' : 'Toca una tarjeta para ver el detalle del colaborador.'}
                    </p>
                </div>

                {canEditRoster && (
                <div className="team-header-actions">
                    <button type="button" className="btn btn-primary btn-sm" onClick={openCreateEmployee}>
                        ➕ Nuevo colaborador
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => {
                            setAreasModalOpen(true);
                            void reloadAreas();
                        }}
                        title="Crear, editar o eliminar áreas de la empresa"
                    >
                        🏭 Gestionar áreas
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => {
                            setCausalesModalOpen(true);
                            void refreshReasons();
                        }}
                        title="Motivos de salida para retiros y reporte CID"
                    >
                        📋 Causales de retiro
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={handleDownloadTemplate}
                        title="Plantilla Excel con documento, nacimiento, salario y campos obligatorios"
                    >
                        📥 Plantilla Excel
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={handleExportRoster}
                        title="Exportar personal activo a Excel"
                    >
                        📤 Exportar Excel
                    </button>

                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => fileInputRef.current.click()}
                        disabled={uploading}
                    >
                        {uploading ? '⏳ Cargando...' : '⬆️ Carga Masiva'}
                    </button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        accept=".xlsx, .xls, .csv"
                        onChange={handleFileChange}
                    />
                </div>
                )}
            </div>

            {loadError ? (
                <div className="glass-card mb-lg" style={{ padding: '1rem 1.25rem', borderLeft: '4px solid var(--color-alert-red, #dc2626)' }}>
                    <p className="font-bold" style={{ margin: '0 0 0.35rem' }}>
                        No se pudo cargar el personal
                    </p>
                    <p className="text-sm text-muted" style={{ margin: 0, lineHeight: 1.45 }}>
                        {loadError}
                    </p>
                    <button type="button" className="btn btn-primary btn-sm mt-md" onClick={() => void loadData()}>
                        Reintentar
                    </button>
                </div>
            ) : null}

            <div className="glass-card team-toolbar mb-lg">
                <div className="team-toolbar-filters">
                    <label className="team-filter-field">
                        <span className="text-xs text-muted">Área</span>
                        <select
                            className="input"
                            value={filterArea}
                            onChange={(e) =>
                                updateFilters({ areaId: e.target.value, supervisorId: filterSupervisor })
                            }
                        >
                            <option value="">Todas las áreas</option>
                            {areaOptions.map((a) => (
                                <option key={a.id} value={a.id}>
                                    {a.name}
                                </option>
                            ))}
                        </select>
                    </label>
                    {!isSupervisorOnly && (
                        <label className="team-filter-field">
                            <span className="text-xs text-muted">Encargado / supervisor</span>
                            <select
                                className="input"
                                value={filterSupervisor}
                                onChange={(e) =>
                                    updateFilters({ areaId: filterArea, supervisorId: e.target.value })
                                }
                            >
                                <option value="">Todos los encargados</option>
                                {supervisors.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.full_name} ({s.employee_code})
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                    {hasActiveFilters && (
                        <button type="button" className="btn btn-secondary btn-sm team-filter-clear" onClick={clearFilters}>
                            Limpiar filtros
                        </button>
                    )}
                </div>
                <div className="team-toolbar-meta">
                    <span className="text-sm text-muted">
                        {filteredEmployees.length} de {employees.length} persona(s)
                    </span>
                    <div className="team-view-toggle" role="group" aria-label="Vista de personal">
                        <button
                            type="button"
                            className={viewMode === 'cards' ? 'active' : ''}
                            onClick={() => setViewMode('cards')}
                            aria-pressed={viewMode === 'cards'}
                        >
                            ⊞ Tarjetas
                        </button>
                        <button
                            type="button"
                            className={viewMode === 'table' ? 'active' : ''}
                            onClick={() => setViewMode('table')}
                            aria-pressed={viewMode === 'table'}
                        >
                            ☰ Tabla
                        </button>
                    </div>
                </div>
            </div>

            {filteredEmployees.length === 0 ? (
                <p className="text-muted text-center py-xl">
                    No hay colaboradores con estos filtros.{' '}
                    {hasActiveFilters && (
                        <button type="button" className="btn btn-link btn-sm" onClick={clearFilters}>
                            Ver todos
                        </button>
                    )}
                </p>
            ) : viewMode === 'cards' ? (
                <div className="team-grid stagger">{filteredEmployees.map(renderEmployeeCard)}</div>
            ) : (
                <div className="team-table-wrap glass-card">
                    <table className="team-table">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Identificación</th>
                                <th>Código</th>
                                <th>Área</th>
                                {!isSupervisorOnly && <th>Encargado</th>}
                                <th>Estado hoy</th>
                                {showSalary && <th>Sueldo</th>}
                                <th>Rol</th>
                                <th aria-label="Acción" />
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEmployees.map((emp) => (
                                <tr
                                    key={emp.id}
                                    className="team-table-row"
                                    onClick={() => handleCardClick(emp)}
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleCardClick(emp);
                                        }
                                    }}
                                >
                                    <td className="font-bold">{emp.full_name}</td>
                                    <td className="font-mono text-sm">{emp.id_document || '—'}</td>
                                    <td className="font-mono text-sm">{emp.employee_code}</td>
                                    <td>{emp.area_name}</td>
                                    {!isSupervisorOnly && <td>{emp.supervisor_name || '—'}</td>}
                                    <td>
                                        <div className="team-table-badges">
                                            {feelingBadge(emp)}
                                            {emp.activeAlerts?.map((a, i) => (
                                                <span key={i} className={`badge badge-${a.level}`}>
                                                    {a.count}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    {showSalary && (
                                        <td className="text-sm">
                                            {Number(emp.monthly_salary) > 0 ? formatCOP(emp.monthly_salary) : '—'}
                                        </td>
                                    )}
                                    <td className="text-sm">{ROLE_LABELS[emp.role] || emp.role}</td>
                                    <td className="team-table-action">⚙️</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {canEditRoster && (
                <EmployeeRosterModal
                    form={rosterModal}
                    setForm={setRosterModal}
                    areas={areas}
                    supervisors={supervisors}
                    saving={rosterSaving}
                    onSubmit={saveRosterEmployee}
                    onClose={() => {
                        if (!rosterSaving) setRosterModal(null);
                    }}
                    onRetire={
                        rosterModal?.mode === 'edit'
                            ? () => {
                                  const emp = employees.find((e) => e.id === rosterModal.id);
                                  if (emp) setRetiringEmp(emp);
                              }
                            : undefined
                    }
                />
            )}

            <AreasManagerModal
                open={areasModalOpen && canEditRoster}
                areas={areas}
                onClose={() => setAreasModalOpen(false)}
                onCreate={handleCreateArea}
                onUpdate={handleUpdateArea}
                onDelete={handleDeleteArea}
                busy={areasBusy}
            />

            {/* RETIREMENT MODAL — Portal to body */}
            {retiringEmp && ReactDOM.createPortal(
                <div
                    style={{ ...TEAM_MODAL_BACKDROP, zIndex: 10001 }}
                    role="presentation"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setRetiringEmp(null);
                    }}
                >
                    <div
                        className="glass-card animate-in"
                        style={{
                            ...TEAM_MODAL_PANEL,
                            maxWidth: 460,
                            borderTop: '4px solid var(--color-alert-red)',
                            backgroundColor: 'var(--color-surface)',
                            padding: '1.5rem',
                        }}
                        role="dialog"
                        aria-modal="true"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="mb-sm">Procesar retiro y costo CID</h3>
                        <p className="text-sm mb-md" style={{ lineHeight: 1.5 }}>
                            Esta acción dará de baja a <strong>{retiringEmp.full_name}</strong>.{' '}
                            <strong>Hoy hay costo financiero</strong> asociado (impacto de desconexión): se calcula con el sueldo
                            registrado y los parámetros de rotación del área y nivel que elijas. El resultado queda en el{' '}
                            <strong>reporte CID</strong> (Indicadores → historial de retiros).
                        </p>
                        <p className="text-xs text-muted mb-lg" style={{ lineHeight: 1.45 }}>
                            Sueldo mensual en manada:{' '}
                            <strong>{formatCOP(retiringEmp.monthly_salary)}</strong>
                            {Number(retiringEmp.monthly_salary) > 0
                                ? ' (COP, usado en la fórmula CID).'
                                : ' — conviene completar el sueldo en “Editar” antes de confirmar para un CID más fiel.'}
                        </p>

                        <form onSubmit={handleRetireSubmit} className="stagger">
                            <div className="form-group mb-sm">
                                <label className="text-xs text-bold">Fecha de Último día</label>
                                <input type="date" name="date" className="input w-100" defaultValue={new Date().toISOString().split('T')[0]} required />
                            </div>

                            <div className="form-group mb-sm">
                                <label className="text-xs text-bold">Causal de Retiro</label>
                                <select name="causal" className="input w-100" required>
                                    <option value="">Seleccione causal</option>
                                    {reasons.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                                </select>
                            </div>

                            <div className="form-group mb-sm">
                                <label className="text-xs text-bold">Confirmar Nivel de Puesto</label>
                                <select name="level" className="input w-100" required defaultValue="operativo">
                                    <option value="operativo">Operativo Base</option>
                                    <option value="tecnico">Técnico Especializado</option>
                                    <option value="supervision">Supervisión / Liderazgo</option>
                                </select>
                            </div>

                            <div className="form-group mb-lg">
                                <label className="text-xs text-bold">Comentarios (Opcional)</label>
                                <textarea name="comments" className="input w-100" rows="2" placeholder="Notas adicionales sobre la salida..."></textarea>
                            </div>

                            <div className="flex gap-sm justify-between">
                                <button type="button" className="btn btn-secondary" onClick={() => setRetiringEmp(null)}>Atrás</button>
                                <button type="submit" className="btn btn-primary" style={{ backgroundColor: 'var(--color-alert-red)', border: 'none' }}>
                                    Confirmar Baja y Calcular CID
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            {causalesModalOpen &&
                canEditRoster &&
                ReactDOM.createPortal(
                    <div
                        className="team-modal-backdrop"
                        role="presentation"
                        onClick={(e) => {
                            if (e.target === e.currentTarget) setCausalesModalOpen(false);
                        }}
                    >
                        <div
                            className="glass-card animate-in team-causales-modal"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="team-causales-title"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="team-causales-modal-head">
                                <div>
                                    <h3 id="team-causales-title">Causales de retiro</h3>
                                    <p className="text-sm text-muted" style={{ margin: '0.35rem 0 0', lineHeight: 1.45 }}>
                                        Motivos de salida usados al procesar una baja y en el reporte CID. Administra el catálogo
                                        de la empresa.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setCausalesModalOpen(false)}
                                    aria-label="Cerrar"
                                >
                                    Cerrar
                                </button>
                            </div>

                            <div className="flex gap-sm team-causales-add">
                                <input
                                    className="input w-100"
                                    placeholder="Nueva causal (ej. Cambio de ciudad)"
                                    value={newCausal}
                                    onChange={(e) => setNewCausal(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            void handleAddCausal();
                                        }
                                    }}
                                    disabled={causalesSaving}
                                />
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={!newCausal.trim() || causalesSaving}
                                    onClick={() => void handleAddCausal()}
                                >
                                    Añadir
                                </button>
                            </div>

                            <div className="team-causales-list">
                                {reasons.length === 0 ? (
                                    <p className="text-muted text-sm text-center py-md">
                                        No hay causales. Añade al menos una para registrar retiros.
                                    </p>
                                ) : (
                                    reasons.map((r) => (
                                        <div key={r.id} className="team-causales-item">
                                            <span>{r.nombre}</span>
                                            <button
                                                type="button"
                                                className="btn btn-outline btn-sm team-causales-del"
                                                disabled={causalesSaving}
                                                onClick={() => void handleDeleteCausal(r.id)}
                                            >
                                                Eliminar
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
}
